import React from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeader, handleApiError } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Search, Plus, Edit2, Trash2, Download, Filter, RefreshCw, MapPin, Loader2, FileText, ChevronLeft, ChevronRight, CheckSquare, Users } from 'lucide-react';

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function ClientesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clientes, setClientes] = React.useState([]);
  const [pagination, setPagination] = React.useState({ page: 1, limit: 50, total: 0, total_pages: 0, has_prev: false, has_next: false });
  const [planos, setPlanos] = React.useState([]);
  const [filiais, setFiliais] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterStatus, setFilterStatus] = React.useState('');
  const [filterFilial, setFilterFilial] = React.useState('');
  const [filterTipoPlano, setFilterTipoPlano] = React.useState(''); // FIBRA / MOVEL
  const [filterDatas, setFilterDatas] = React.useState(''); // proxima_recarga, expiracao_dados, vencendo_7dias
  const [filterFromDashboard, setFilterFromDashboard] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [selectedCliente, setSelectedCliente] = React.useState(null);
  const [loadingCep, setLoadingCep] = React.useState(false);
  const [boletoModalOpen, setBoletoModalOpen] = React.useState(false);
  const [boletoCliente, setBoletoCliente] = React.useState(null);
  const [boletoForm, setBoletoForm] = React.useState({ valor: '', vencimento: '', descricao: '' });
  const [loadingBoleto, setLoadingBoleto] = React.useState(false);
  const [formData, setFormData] = React.useState({
    nome: '', nome_fantasia: '', telefone: '', whatsapp: '', email: '', cpf_cnpj: '', tipo_pessoa: 'PF',
    data_nascimento: '', cep: '', endereco: '', numero: '', bairro: '', cidade: '', uf: '',
    filial_id: '', plano_id: '', tipo_plano: 'MOVEL', valor: '', custo: '', status: 'ATIVO', status_financeiro: 'EM_DIA',
    tipo_chip: '', tipo_venda: '', cliente_combo: false, desconto_combo: 0, socio: false,
    data_ativacao: '', vencimento: '', proxima_recarga: '', expiracao_dados: '', observacoes: ''
  });

  // Estados para edição em massa
  const [selectedClientes, setSelectedClientes] = React.useState([]);
  const [bulkEditModalOpen, setBulkEditModalOpen] = React.useState(false);
  const [bulkEditLoading, setBulkEditLoading] = React.useState(false);
  const [bulkEditData, setBulkEditData] = React.useState({
    filial_id: '',
    socio: '',
    tipo_venda: '',
    tipo_chip: ''
  });

  // Ler filtro da URL (vindo do Dashboard)
  React.useEffect(function() {
    var filtro = searchParams.get('filtro');
    if (filtro) {
      setFilterFromDashboard(filtro);
      // Aplicar filtros baseado no parâmetro
      switch (filtro) {
        case 'fibra':
          setFilterTipoPlano('FIBRA');
          setFilterDatas('');
          break;
        case 'movel':
          setFilterTipoPlano('MOVEL');
          setFilterDatas('');
          break;
        case 'ativos':
          setFilterStatus('EM_DIA');
          setFilterDatas('');
          break;
        case 'em_atraso':
          setFilterStatus('EM_ATRASO');
          setFilterDatas('');
          break;
        case 'combo':
          setFilterDatas('');
          break;
        case 'vencendo_hoje':
          setFilterTipoPlano('');
          setFilterDatas('vencendo_hoje');
          break;
        case 'vencendo_3dias':
          setFilterTipoPlano('');
          setFilterDatas('vencendo_3dias');
          break;
        case 'vencendo_7dias':
          setFilterTipoPlano('');
          setFilterDatas('vencendo_7dias');
          break;
        case 'vencidos':
          setFilterTipoPlano('');
          setFilterDatas('vencidos');
          break;
        default:
          break;
      }
    }
  }, [searchParams]);

  React.useEffect(function() {
    fetchPlanos();
    fetchFiliais();
  }, []);

  // Buscar clientes quando filtros mudam (mas só após o filtro do dashboard ser processado)
  React.useEffect(function() {
    // Não buscar se o filtro do dashboard ainda não foi processado
    var filtro = searchParams.get('filtro');
    if (filtro && !filterFromDashboard) {
      // Aguardar o useEffect do searchParams processar primeiro
      return;
    }
    fetchClientes(1);
  }, [searchTerm, filterStatus, filterFilial, filterTipoPlano, filterDatas, filterFromDashboard]);

  // Função para verificar se uma data está nos próximos X dias
  function isDateInNextDays(dateStr, days) {
    if (!dateStr) return false;
    var date = new Date(dateStr);
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var futureDate = new Date(today);
    futureDate.setDate(futureDate.getDate() + days);
    return date >= today && date <= futureDate;
  }

  // Função para filtrar clientes por outros filtros do dashboard (apenas os que NÃO são filtrados no backend)
  function getFilteredClientes() {
    var resultado = clientes;
    
    // Filtro especial: Combo (Fibra + Móvel) - já é filtrado no backend via cliente_combo=true
    // Outros filtros de data são feitos no backend via filtro_data
    
    return resultado;
  }

  // Função para limpar filtros
  function clearFilters() {
    setFilterTipoPlano('');
    setFilterStatus('');
    setFilterFilial('');
    setFilterDatas('');
    setFilterFromDashboard('');
    setSearchTerm('');
    setSearchParams({});
    fetchClientes(1);
  }

  // Função para obter título do filtro ativo
  function getFilterTitle() {
    switch (filterFromDashboard) {
      case 'todos': return 'Todos os Clientes';
      case 'fibra': return 'Clientes Fibra';
      case 'movel': return 'Clientes Telefonia Móvel';
      case 'ativos': return 'Clientes Ativos';
      case 'em_atraso': return 'Clientes em Atraso';
      case 'combo': return 'Clientes Combo';
      case 'vencendo_hoje': return '🚨 VENCE HOJE - Móvel';
      case 'vencendo_3dias': return '⚠️ Vencendo em 3 dias - Móvel';
      case 'vencendo_7dias': return 'Vencendo em 7 dias - Móvel';
      case 'vencidos': return '🔴 VENCIDOS - Móvel';
      default: return 'CLIENTES';
    }
  }

  function fetchClientes(page) {
    setLoading(true);
    var params = new URLSearchParams();
    var pageNum = (typeof page === 'number' && page > 0) ? page : pagination.page || 1;
    params.append('page', pageNum);
    params.append('limit', 50);
    if (searchTerm) params.append('search', searchTerm);
    if (filterStatus && filterStatus !== 'all') params.append('status_financeiro', filterStatus);
    if (filterFilial && filterFilial !== 'all') params.append('filial', filterFilial);
    if (filterTipoPlano) params.append('tipo_plano', filterTipoPlano);
    if (filterDatas) params.append('filtro_data', filterDatas);
    if (filterFromDashboard === 'combo') params.append('cliente_combo', 'true');
    
    axios.get(API + '/clientes?' + params.toString(), { headers: getAuthHeader() })
      .then(function(response) { 
        setClientes(response.data.clientes || []); 
        setPagination(response.data.pagination || { page: 1, limit: 50, total: 0, total_pages: 0, has_prev: false, has_next: false });
      })
      .catch(function(error) { 
        handleApiError(error);
        toast.error('Erro ao carregar clientes'); 
      })
      .finally(function() { setLoading(false); });
  }

  function goToPage(page) {
    if (page < 1 || page > pagination.total_pages) return;
    fetchClientes(page);
  }

  function fetchPlanos() {
    axios.get(API + '/planos', { headers: getAuthHeader() })
      .then(function(response) { setPlanos(response.data); })
      .catch(function(error) { 
        handleApiError(error);
        console.error('Erro ao carregar planos'); 
      });
  }

  function fetchFiliais() {
    axios.get(API + '/filiais', { headers: getAuthHeader() })
      .then(function(response) { setFiliais(response.data); })
      .catch(function(error) { 
        handleApiError(error);
        console.error('Erro ao carregar filiais'); 
      });
  }

  function buscarCep(cep) {
    var cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    
    setLoadingCep(true);
    axios.get('https://viacep.com.br/ws/' + cepLimpo + '/json/')
      .then(function(response) {
        if (response.data.erro) {
          toast.error('CEP não encontrado');
          return;
        }
        setFormData(function(prev) {
          return Object.assign({}, prev, {
            endereco: response.data.logradouro || '',
            bairro: response.data.bairro || '',
            cidade: response.data.localidade || '',
            estado: response.data.uf || ''
          });
        });
        toast.success('Endereço preenchido!');
      })
      .catch(function() { toast.error('Erro ao buscar CEP'); })
      .finally(function() { setLoadingCep(false); });
  }

  function openCreateModal() {
    setSelectedCliente(null);
    setFormData({
      nome: '', nome_fantasia: '', telefone: '', whatsapp: '', email: '', cpf_cnpj: '', tipo_pessoa: 'PF',
      data_nascimento: '', cep: '', endereco: '', numero: '', bairro: '', cidade: '', uf: '',
      filial_id: '', plano_id: '', tipo_plano: 'MOVEL', valor: '', custo: '', status: 'ATIVO', status_financeiro: 'EM_DIA',
      tipo_chip: '', tipo_venda: '', cliente_combo: false, desconto_combo: 0,
      data_ativacao: '', vencimento: '', proxima_recarga: '', expiracao_dados: '', observacoes: ''
    });
    setModalOpen(true);
  }

  function openEditModal(cliente) {
    setSelectedCliente(cliente);
    // Busca filial por código ou nome (comparação case-insensitive)
    var filialObj = filiais.find(function(f) { 
      if (!cliente.filial) return false;
      var clienteFilial = String(cliente.filial).trim().toUpperCase();
      var filialCodigo = String(f.codigo || '').trim().toUpperCase();
      var filialNome = String(f.nome || '').trim().toUpperCase();
      return filialCodigo === clienteFilial || 
             filialNome === clienteFilial || 
             clienteFilial.includes(filialNome) ||
             filialNome.includes(clienteFilial);
    });
    var planoObj = planos.find(function(p) { return p.nome === cliente.plano; });
    
    setFormData({
      nome: cliente.nome || '',
      nome_fantasia: cliente.nome_fantasia || '',
      telefone: cliente.telefone || '',
      whatsapp: cliente.whatsapp || cliente.telefone || '',
      email: cliente.email || '',
      cpf_cnpj: cliente.cpf_cnpj || '',
      tipo_pessoa: cliente.tipo_pessoa || 'PF',
      data_nascimento: cliente.data_nascimento || '',
      cep: cliente.cep || '',
      endereco: cliente.endereco || '',
      numero: cliente.numero || '',
      bairro: cliente.bairro || '',
      cidade: cliente.cidade || '',
      uf: cliente.uf || '',
      filial_id: filialObj ? filialObj.id : '',
      plano_id: planoObj ? planoObj.id : '',
      tipo_plano: cliente.tipo_plano || 'MOVEL',
      valor: cliente.valor || '',
      custo: cliente.custo || '',
      status: cliente.status || 'ATIVO',
      status_financeiro: cliente.status_financeiro || 'EM_DIA',
      tipo_chip: cliente.tipo_chip || '',
      tipo_venda: cliente.tipo_venda || '',
      cliente_combo: cliente.cliente_combo || false,
      socio: cliente.socio || false,
      desconto_combo: cliente.desconto_combo || 0,
      data_ativacao: cliente.data_ativacao || '',
      vencimento: cliente.vencimento || '',
      proxima_recarga: cliente.proxima_recarga || '',
      expiracao_dados: cliente.expiracao_dados || '',
      observacoes: cliente.observacoes || ''
    });
    setModalOpen(true);
  }

  function handleSubmit(e) {
    e.preventDefault();
    
    var filialSelecionada = filiais.find(function(f) { return f.id === formData.filial_id; });
    var planoSelecionado = planos.find(function(p) { return p.id === formData.plano_id; });
    
    // Preservar filial original se não foi alterada
    var filialFinal = filialSelecionada ? filialSelecionada.codigo : (selectedCliente ? selectedCliente.filial : '');
    
    // Calcular valor com desconto combo se aplicável
    var valorFinal = planoSelecionado ? planoSelecionado.valor : (formData.valor ? parseFloat(formData.valor) : null);
    if (formData.cliente_combo && planoSelecionado && planoSelecionado.desconto_combo > 0) {
      valorFinal = Math.max(0, planoSelecionado.valor - planoSelecionado.desconto_combo);
    }
    
    // Calcular próxima recarga, vencimento e expiração baseado na data de ativação
    // REGRA APENAS PARA PLANOS MÓVEIS (telefonia)
    var proximaRecarga = formData.proxima_recarga;
    var vencimento = formData.vencimento;
    var expiracaoDados = formData.expiracao_dados;
    
    if (formData.data_ativacao && formData.tipo_plano === 'MOVEL') {
      var dataAtiv = new Date(formData.data_ativacao);
      
      // Expiração de Dados: Ativação + 30 dias (se não informado)
      if (!expiracaoDados) {
        var dataExp = new Date(dataAtiv);
        dataExp.setDate(dataExp.getDate() + 30);
        expiracaoDados = dataExp.toISOString().split('T')[0];
      }
      
      // Próxima Recarga: Ativação + 25 dias (se não informado)
      if (!proximaRecarga) {
        var dataRec = new Date(dataAtiv);
        dataRec.setDate(dataRec.getDate() + 25);
        proximaRecarga = dataRec.toISOString().split('T')[0];
      }
      
      // Vencimento igual à próxima recarga para MOVEL
      if (!vencimento) {
        vencimento = proximaRecarga;
      }
    }
    
    var data = {
      nome: formData.nome,
      nome_fantasia: formData.nome_fantasia || null,
      telefone: formData.telefone,
      whatsapp: formData.whatsapp || formData.telefone || null,
      email: formData.email || null,
      cpf_cnpj: formData.cpf_cnpj || null,
      tipo_pessoa: formData.tipo_pessoa || 'PF',
      data_nascimento: formData.data_nascimento || null,
      endereco: formData.endereco || null,
      numero: formData.numero || null,
      bairro: formData.bairro || null,
      cidade: formData.cidade || null,
      uf: formData.uf || null,
      cep: formData.cep || null,
      filial: filialFinal,
      plano: planoSelecionado ? planoSelecionado.nome : '',
      tipo_plano: formData.tipo_plano || 'MOVEL',
      valor: valorFinal,
      custo: planoSelecionado ? planoSelecionado.custo : (formData.custo ? parseFloat(formData.custo) : null),
      status: formData.status || 'ATIVO',
      status_financeiro: formData.status_financeiro,
      tipo_chip: formData.tipo_chip,
      tipo_venda: formData.tipo_venda,
      cliente_combo: formData.cliente_combo,
      socio: formData.socio || false,
      data_ativacao: formData.data_ativacao || null,
      vencimento: vencimento || null,
      proxima_recarga: proximaRecarga || null,
      expiracao_dados: expiracaoDados || null,
      observacoes: formData.observacoes
    };
    
    var request = selectedCliente
      ? axios.put(API + '/clientes/' + selectedCliente.id, data, { headers: getAuthHeader() })
      : axios.post(API + '/clientes', data, { headers: getAuthHeader() });
    
    request.then(function() {
      toast.success(selectedCliente ? 'Atualizado!' : 'Criado!');
      setModalOpen(false);
      fetchClientes(pagination.page);
    }).catch(function() { toast.error('Erro ao salvar'); });
  }

  function handleDelete() {
    axios.delete(API + '/clientes/' + selectedCliente.id, { headers: getAuthHeader() })
      .then(function() { toast.success('Excluído!'); setDeleteModalOpen(false); fetchClientes(pagination.page); })
      .catch(function() { toast.error('Erro'); });
  }

  function exportData() {
    axios.get(API + '/export/clientes', { headers: getAuthHeader() })
      .then(function(response) {
        var dataStr = JSON.stringify(response.data, null, 2);
        var dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        var link = document.createElement('a');
        link.setAttribute('href', dataUri);
        link.setAttribute('download', 'clientes.json');
        link.click();
        toast.success('Exportado!');
      }).catch(function() { toast.error('Erro'); });
  }

  function updateForm(field, value) {
    setFormData(function(prev) { var newData = Object.assign({}, prev); newData[field] = value; return newData; });
  }
  
  // Função para atualizar data de ativação e calcular datas automaticamente (só para MOVEL)
  function handleDataAtivacaoChange(value) {
    setFormData(function(prev) {
      var newData = Object.assign({}, prev);
      newData.data_ativacao = value;
      
      // Só calcular automaticamente se for plano MOVEL
      if (value && prev.tipo_plano === 'MOVEL') {
        var dataAtiv = new Date(value);
        
        // Expiração de Dados: Ativação + 30 dias
        var dataExp = new Date(dataAtiv);
        dataExp.setDate(dataExp.getDate() + 30);
        newData.expiracao_dados = dataExp.toISOString().split('T')[0];
        
        // Próxima Recarga: Ativação + 25 dias
        var dataRec = new Date(dataAtiv);
        dataRec.setDate(dataRec.getDate() + 25);
        newData.proxima_recarga = dataRec.toISOString().split('T')[0];
        
        // Vencimento igual à próxima recarga
        newData.vencimento = newData.proxima_recarga;
      }
      
      return newData;
    });
  }
  
  // Função para atualizar tipo de plano e recalcular datas se necessário
  function handleTipoPlanoChange(value) {
    setFormData(function(prev) {
      var newData = Object.assign({}, prev);
      newData.tipo_plano = value;
      
      // Se mudou para MOVEL e tem data de ativação, calcular datas
      if (value === 'MOVEL' && prev.data_ativacao) {
        var dataAtiv = new Date(prev.data_ativacao);
        
        // Expiração de Dados: Ativação + 30 dias
        var dataExp = new Date(dataAtiv);
        dataExp.setDate(dataExp.getDate() + 30);
        newData.expiracao_dados = dataExp.toISOString().split('T')[0];
        
        // Próxima Recarga: Ativação + 25 dias
        var dataRec = new Date(dataAtiv);
        dataRec.setDate(dataRec.getDate() + 25);
        newData.proxima_recarga = dataRec.toISOString().split('T')[0];
        
        // Vencimento igual à próxima recarga
        newData.vencimento = newData.proxima_recarga;
      }
      
      return newData;
    });
  }

  function handlePlanoChange(planoId) {
    var plano = planos.find(function(p) { return p.id === planoId; });
    setFormData(function(prev) {
      var novoValor = plano ? plano.valor : prev.valor;
      // Aplicar desconto combo se checkbox estiver marcado
      if (prev.cliente_combo && plano && plano.desconto_combo > 0) {
        novoValor = Math.max(0, plano.valor - plano.desconto_combo);
      }
      return Object.assign({}, prev, {
        plano_id: planoId,
        valor: novoValor,
        custo: plano ? plano.custo : prev.custo,
        desconto_combo: plano ? (plano.desconto_combo || 0) : 0
      });
    });
  }

  function handleComboChange(checked) {
    var plano = planos.find(function(p) { return p.id === formData.plano_id; });
    setFormData(function(prev) {
      var novoValor = prev.valor;
      if (plano) {
        if (checked && plano.desconto_combo > 0) {
          novoValor = Math.max(0, plano.valor - plano.desconto_combo);
        } else {
          novoValor = plano.valor;
        }
      }
      return Object.assign({}, prev, {
        cliente_combo: checked,
        valor: novoValor,
        desconto_combo: checked && plano ? (plano.desconto_combo || 0) : 0
      });
    });
  }

  function openBoletoModal(cliente) {
    setBoletoCliente(cliente);
    setBoletoForm({
      valor: cliente.valor || '',
      vencimento: '',
      descricao: 'Mensalidade - ' + (cliente.plano || 'Serviço')
    });
    setBoletoModalOpen(true);
  }

  function handleBoletoSubmit(e) {
    e.preventDefault();
    if (!boletoForm.vencimento) {
      toast.error('Informe a data de vencimento');
      return;
    }
    
    setLoadingBoleto(true);
    axios.post(API + '/lytex/boletos', {
      cliente_id: boletoCliente.id,
      valor: boletoForm.valor ? parseFloat(boletoForm.valor) : null,
      vencimento: boletoForm.vencimento,
      descricao: boletoForm.descricao || null
    }, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success('Boleto gerado com sucesso!');
        setBoletoModalOpen(false);
        // Abrir a fatura em nova aba se disponível
        if (response.data.invoice_url) {
          window.open(response.data.invoice_url, '_blank');
        }
      })
      .catch(function(error) {
        toast.error('Erro ao gerar boleto: ' + (error.response?.data?.detail || error.message));
      })
      .finally(function() {
        setLoadingBoleto(false);
      });
  }

  // Funções para edição em massa
  function toggleSelectCliente(clienteId) {
    setSelectedClientes(function(prev) {
      if (prev.includes(clienteId)) {
        return prev.filter(function(id) { return id !== clienteId; });
      } else {
        return [...prev, clienteId];
      }
    });
  }

  function toggleSelectAll() {
    var clientesVisiveis = getFilteredClientes();
    if (selectedClientes.length === clientesVisiveis.length) {
      setSelectedClientes([]);
    } else {
      setSelectedClientes(clientesVisiveis.map(function(c) { return c.id; }));
    }
  }

  function openBulkEditModal() {
    setBulkEditData({ filial_id: '', socio: '', tipo_venda: '', tipo_chip: '' });
    setBulkEditModalOpen(true);
  }

  function handleBulkEditSubmit() {
    // Preparar dados - só envia campos que foram preenchidos
    var updateData = {};
    if (bulkEditData.filial_id) {
      var filialSelecionada = filiais.find(function(f) { return f.id === bulkEditData.filial_id; });
      if (filialSelecionada) {
        updateData.filial = filialSelecionada.nome || filialSelecionada.codigo;
      }
    }
    if (bulkEditData.socio !== '') {
      updateData.socio = bulkEditData.socio === 'true';
    }
    if (bulkEditData.tipo_venda) {
      updateData.tipo_venda = bulkEditData.tipo_venda;
    }
    if (bulkEditData.tipo_chip) {
      updateData.tipo_chip = bulkEditData.tipo_chip;
    }
    
    if (Object.keys(updateData).length === 0) {
      toast.error('Selecione pelo menos um campo para atualizar');
      return;
    }
    
    setBulkEditLoading(true);
    axios.post(API + '/clientes/bulk-update', {
      cliente_ids: selectedClientes,
      update_data: updateData
    }, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success('Clientes atualizados: ' + response.data.updated_count);
        setBulkEditModalOpen(false);
        setSelectedClientes([]);
        fetchClientes(pagination.page);
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao atualizar clientes');
      })
      .finally(function() {
        setBulkEditLoading(false);
      });
  }

  // Função para corrigir filiais dos clientes MOVEL automaticamente
  function corrigirFiliaisMovel() {
    if (!window.confirm('Isso vai corrigir as filiais de todos os clientes MOVEL:\n\n• DDD 11 (exceto Ailton) → São Paulo\n• Demais → Passa Quatro\n\nDeseja continuar?')) {
      return;
    }
    
    setLoading(true);
    axios.post(API + '/clientes/corrigir-filiais-movel', {}, { headers: getAuthHeader() })
      .then(function(response) {
        toast.success(response.data.message);
        fetchClientes(pagination.page);
      })
      .catch(function(error) {
        handleApiError(error);
        toast.error('Erro ao corrigir filiais');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">{getFilterTitle()}</h1>
          <p className="text-slate-500 text-sm sm:text-base">
            {getFilteredClientes().length} clientes 
            {filterFromDashboard && filterFromDashboard !== 'todos' && ' (filtrado)'}
            {!filterFromDashboard && ` de ${pagination.total}`}
          </p>
          {filterFromDashboard && filterFromDashboard !== 'todos' && (
            <Button onClick={clearFilters} variant="outline" size="sm" className="mt-1 text-orange-600 border-orange-300 hover:bg-orange-50">
              ✕ Limpar
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          {/* Botão Corrigir Filiais MOVEL - apenas para Super Admin quando filtra por MOVEL */}
          {filterTipoPlano === 'MOVEL' && (
            <Button 
              data-testid="fix-filiais-btn" 
              onClick={corrigirFiliaisMovel} 
              size="sm" 
              variant="outline"
              className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
              title="Corrige automaticamente as filiais dos clientes MOVEL baseado no DDD"
            >
              <MapPin className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Corrigir Filiais</span>
            </Button>
          )}
          {selectedClientes.length > 0 && (
            <Button data-testid="bulk-edit-btn" onClick={openBulkEditModal} size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
              <CheckSquare className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Editar {selectedClientes.length}</span>
              <span className="sm:hidden">{selectedClientes.length}</span>
            </Button>
          )}
          <Button data-testid="export-btn" onClick={exportData} variant="outline" size="sm" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">
            <Download className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Exportar</span>
          </Button>
          <Button data-testid="add-btn" onClick={openCreateModal} size="sm" className="bg-h4p-blue hover:bg-h4p-blue-hover text-white">
            <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Novo</span>
          </Button>
        </div>
      </div>

      {/* Área de Busca e Filtros - Design Moderno com tons de laranja */}
      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200 shadow-md overflow-hidden">
        <CardContent className="p-0">
          {/* Barra principal de busca */}
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-orange-200" />
                <Input 
                  placeholder="Buscar..." 
                  value={searchTerm} 
                  onChange={function(e) { setSearchTerm(e.target.value); }} 
                  className="pl-10 sm:pl-12 bg-white/90 border-0 text-slate-700 placeholder:text-slate-400 h-10 sm:h-12 rounded-lg shadow-sm focus:bg-white focus:ring-2 focus:ring-orange-300 text-sm" 
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  onClick={function() { setShowFilters(!showFilters); }} 
                  size="sm"
                  className={`h-10 sm:h-12 px-3 sm:px-5 rounded-lg font-medium transition-all ${showFilters ? 'bg-white text-orange-600 hover:bg-orange-50' : 'bg-white/20 text-white hover:bg-white/30 border border-white/30'}`}
                >
                  <Filter className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Filtros</span>
                </Button>
                <Button onClick={function() { fetchClientes(1); }} size="sm" className="h-10 sm:h-12 px-4 sm:px-6 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium shadow-lg">
                  Buscar
                </Button>
              </div>
            </div>
          </div>
          
          {/* Filtros expandidos */}
          {showFilters && (
            <div className="p-4 bg-white/80 border-b border-orange-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-orange-700 text-sm mb-2 block font-medium">Filial</Label>
                  <Select value={filterFilial} onValueChange={setFilterFilial}>
                    <SelectTrigger className="bg-white border-orange-200 text-slate-700 hover:border-orange-400"><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent className="bg-white border-orange-200 z-[100]">
                      <SelectItem value="all">Todas</SelectItem>
                      {filiais.map(function(f) { return <SelectItem key={f.id} value={f.codigo}>{f.nome} ({f.codigo})</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-orange-700 text-sm mb-2 block font-medium">Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-white border-orange-200 text-slate-700 hover:border-orange-400"><SelectValue placeholder="Todos" /></SelectTrigger>
                    <SelectContent className="bg-white border-orange-200 z-[100]">
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="EM_DIA">Em Dia</SelectItem>
                      <SelectItem value="EM_ATRASO">Em Atraso</SelectItem>
                      <SelectItem value="BLOQUEADO">Bloqueado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={function() { setSearchTerm(''); setFilterStatus(''); setFilterFilial(''); setFilterDatas(''); }} variant="ghost" className="text-orange-600 hover:text-orange-700 hover:bg-orange-50">
                    <RefreshCw className="w-4 h-4 mr-2" />Limpar Filtros
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Filtros Rápidos por Datas */}
          <div className="p-4 bg-white/60">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-1 h-5 bg-orange-500 rounded-full"></div>
              <Label className="text-slate-700 text-sm font-semibold uppercase tracking-wide">Filtros Rápidos</Label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={function() { setFilterDatas(filterDatas === 'recarga_7dias' ? '' : 'recarga_7dias'); }} 
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all shadow-sm ${filterDatas === 'recarga_7dias' ? 'bg-amber-500 text-white shadow-amber-200' : 'bg-white text-amber-600 border-2 border-amber-200 hover:border-amber-400 hover:bg-amber-50'}`}
              >
                🔄 Recarga 7 dias
              </Button>
              <Button 
                onClick={function() { setFilterDatas(filterDatas === 'recarga_15dias' ? '' : 'recarga_15dias'); }} 
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all shadow-sm ${filterDatas === 'recarga_15dias' ? 'bg-amber-500 text-white shadow-amber-200' : 'bg-white text-amber-600 border-2 border-amber-200 hover:border-amber-400 hover:bg-amber-50'}`}
              >
                🔄 Recarga 15 dias
              </Button>
              <Button 
                onClick={function() { setFilterDatas(filterDatas === 'expiracao_7dias' ? '' : 'expiracao_7dias'); }} 
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all shadow-sm ${filterDatas === 'expiracao_7dias' ? 'bg-red-500 text-white shadow-red-200' : 'bg-white text-red-500 border-2 border-red-200 hover:border-red-400 hover:bg-red-50'}`}
              >
                ⚠️ Exp. Dados 7 dias
              </Button>
              <Button 
                onClick={function() { setFilterDatas(filterDatas === 'expiracao_15dias' ? '' : 'expiracao_15dias'); }} 
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all shadow-sm ${filterDatas === 'expiracao_15dias' ? 'bg-red-500 text-white shadow-red-200' : 'bg-white text-red-500 border-2 border-red-200 hover:border-red-400 hover:bg-red-50'}`}
              >
                ⚠️ Exp. Dados 15 dias
              </Button>
              <Button 
                onClick={function() { setFilterDatas(filterDatas === 'vencidos' ? '' : 'vencidos'); }} 
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all shadow-sm ${filterDatas === 'vencidos' ? 'bg-slate-800 text-white shadow-slate-300' : 'bg-white text-slate-600 border-2 border-slate-300 hover:border-slate-400 hover:bg-slate-50'}`}
              >
                🚫 Vencidos
              </Button>
            </div>
            {filterDatas && (
              <div className="mt-3 inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-full text-sm font-medium">
                <span>Exibindo: {getFilteredClientes().length} cliente(s)</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-slate-200 shadow-sm overflow-hidden">
        <div className="max-h-[calc(100vh-320px)] overflow-auto">
          <table className="w-full caption-bottom text-sm">
            <thead className="sticky top-0 z-10 bg-slate-800">
              <tr className="border-b border-slate-700">
                <th className="h-10 px-2 text-center align-middle py-4 bg-slate-800 w-10">
                  <Checkbox 
                    checked={selectedClientes.length > 0 && selectedClientes.length === getFilteredClientes().length}
                    onCheckedChange={toggleSelectAll}
                    className="border-white data-[state=checked]:bg-purple-500"
                  />
                </th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Nome</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Data Ativação</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Próx. Recarga</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Exp. Dados</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Telefone</th>
                {filterTipoPlano === 'MOVEL' && <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Linha/SIM</th>}
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Filial</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Plano</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Valor</th>
                <th className="h-10 px-2 text-left align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Status</th>
                <th className="h-10 px-2 text-right align-middle font-bold text-sm uppercase tracking-wide py-4 text-white bg-slate-800">Ações</th>
              </tr>
            </thead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto"></div></TableCell></TableRow>
              ) : getFilteredClientes().length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-400">Nenhum cliente encontrado</TableCell></TableRow>
              ) : getFilteredClientes().map(function(cliente, index) {
                var formatDateBR = function(d) { if (!d) return '-'; var p = d.split('-'); return p[2] + '/' + p[1] + '/' + p[0]; };
                // Verificar se está vencido
                var hoje = new Date();
                hoje.setHours(0, 0, 0, 0);
                var dataVencimento = cliente.proxima_recarga || cliente.vencimento;
                var vencido = dataVencimento && new Date(dataVencimento) < hoje;
                var proximoVencer = dataVencimento && !vencido && isDateInNextDays(dataVencimento, 7);
                
                return (
                  <TableRow key={cliente.id} className={`border-slate-200 hover:bg-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${selectedClientes.includes(cliente.id) ? 'bg-purple-50' : ''}`}>
                    <TableCell className="w-10 text-center">
                      <Checkbox 
                        checked={selectedClientes.includes(cliente.id)}
                        onCheckedChange={function() { toggleSelectCliente(cliente.id); }}
                        className="data-[state=checked]:bg-purple-500"
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-slate-800 font-medium">{cliente.nome}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium text-sm ${cliente.data_ativacao ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {formatDateBR(cliente.data_ativacao)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium text-sm ${vencido ? 'text-red-600' : proximoVencer ? 'text-amber-500' : 'text-sky-600'}`}>
                        {formatDateBR(cliente.proxima_recarga)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium text-sm ${cliente.expiracao_dados ? (new Date(cliente.expiracao_dados) < hoje ? 'text-red-600' : 'text-emerald-600') : 'text-slate-400'}`}>
                        {formatDateBR(cliente.expiracao_dados)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-600 text-sm">{cliente.telefone || cliente.whatsapp || '-'}</span>
                    </TableCell>
                    {filterTipoPlano === 'MOVEL' && (
                    <TableCell>
                      <div className="text-xs">
                        {cliente.numero_linha && <p className="text-slate-700 font-medium">{cliente.numero_linha}</p>}
                        {cliente.simcard && <p className="text-slate-400 truncate max-w-[120px]" title={cliente.simcard}>{cliente.simcard}</p>}
                        {!cliente.numero_linha && !cliente.simcard && <span className="text-slate-400">-</span>}
                      </div>
                    </TableCell>
                    )}
                    <TableCell>
                      <span className="bg-indigo-100 text-indigo-600 text-xs px-2 py-1 rounded-full font-medium">
                        {cliente.filial || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${cliente.tipo_plano === 'MOVEL' ? 'bg-purple-100 text-purple-600' : cliente.tipo_plano === 'SOCIOS' ? 'bg-green-100 text-green-600' : 'bg-orange-100 text-orange-600'}`}>
                        {cliente.plano || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-slate-800 font-semibold">
                        {cliente.valor ? 'R$ ' + Number(cliente.valor).toFixed(2).replace('.', ',') : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={
                        (cliente.status === 'ATIVO' && cliente.status_financeiro === 'EM_DIA') ? 'bg-emerald-100 text-emerald-600 text-xs px-2 py-1 rounded-full font-medium' : 
                        (cliente.status === 'BLOQUEADO' || cliente.status_financeiro === 'BLOQUEADO') ? 'bg-gray-800 text-white text-xs px-2 py-1 rounded-full font-medium' :
                        cliente.status === 'INATIVO' ? 'bg-slate-200 text-slate-600 text-xs px-2 py-1 rounded-full font-medium' :
                        'bg-red-100 text-red-600 text-xs px-2 py-1 rounded-full font-medium'
                      }>
                        {cliente.status === 'BLOQUEADO' || cliente.status_financeiro === 'BLOQUEADO' ? 'Bloqueado' : 
                         cliente.status === 'INATIVO' ? 'Inativo' :
                         cliente.status_financeiro === 'EM_ATRASO' ? 'Em Atraso' : 'Em Dia'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button onClick={function() { openBoletoModal(cliente); }} variant="ghost" size="sm" className="text-green-500 hover:text-green-600 hover:bg-green-50" title="Gerar Boleto">
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button onClick={function() { openEditModal(cliente); }} variant="ghost" size="sm" className="text-slate-400 hover:text-sky-500 hover:bg-sky-50">
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button onClick={function() { setSelectedCliente(cliente); setDeleteModalOpen(true); }} variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </table>
        </div>
        
        {/* Paginação - Responsiva */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-lg gap-3">
          <div className="text-sm text-slate-600 text-center sm:text-left">
            <span className="hidden sm:inline">Mostrando </span>
            <span className="font-semibold">{((pagination.page - 1) * pagination.limit) + 1}</span>
            <span> - </span>
            <span className="font-semibold">{Math.min(pagination.page * pagination.limit, pagination.total)}</span>
            <span> de </span>
            <span className="font-semibold">{pagination.total}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={function() { goToPage(pagination.page - 1); }} 
              disabled={!pagination.has_prev || loading}
              className="bg-white border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Anterior</span>
            </Button>
            <div className="flex items-center gap-1">
              <span className="text-sm text-slate-600 px-2">{pagination.page} / {pagination.total_pages}</span>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={function() { goToPage(pagination.page + 1); }} 
              disabled={!pagination.has_next || loading}
              className="bg-white border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
            >
              <span className="hidden sm:inline mr-1">Próximo</span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-800">{selectedCliente ? 'Editar' : 'Novo'} Cliente</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dados Pessoais */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Dados Pessoais</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <Label className="text-slate-600">Nome / Razão Social *</Label>
                  <Input value={formData.nome} onChange={function(e) { updateForm('nome', e.target.value); }} className="bg-white border-slate-300 text-slate-700" required />
                </div>
                <div>
                  <Label className="text-slate-600">Tipo</Label>
                  <Select value={formData.tipo_pessoa} onValueChange={function(v) { updateForm('tipo_pessoa', v); }}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="PF">Pessoa Física</SelectItem>
                      <SelectItem value="PJ">Pessoa Jurídica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Nome Fantasia</Label>
                  <Input value={formData.nome_fantasia} onChange={function(e) { updateForm('nome_fantasia', e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-600">CPF/CNPJ</Label>
                  <Input value={formData.cpf_cnpj} onChange={function(e) { updateForm('cpf_cnpj', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="000.000.000-00" />
                </div>
                <div>
                  <Label className="text-slate-600">Data Nascimento</Label>
                  <Input type="date" value={formData.data_nascimento} onChange={function(e) { updateForm('data_nascimento', e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
                </div>
              </div>
            </div>
            
            {/* Contato */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Contato</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600">Telefone</Label>
                  <Input value={formData.telefone} onChange={function(e) { updateForm('telefone', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="(00) 00000-0000" />
                </div>
                <div>
                  <Label className="text-slate-600">WhatsApp</Label>
                  <Input value={formData.whatsapp} onChange={function(e) { updateForm('whatsapp', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="(00) 00000-0000" />
                </div>
                <div>
                  <Label className="text-slate-600">Email</Label>
                  <Input type="email" value={formData.email} onChange={function(e) { updateForm('email', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="email@exemplo.com" />
                </div>
              </div>
            </div>
            
            {/* Endereço */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Endereço</h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label className="text-slate-600">CEP</Label>
                  <div className="relative">
                    <Input 
                      value={formData.cep} 
                      onChange={function(e) { updateForm('cep', e.target.value); }} 
                      onBlur={function(e) { buscarCep(e.target.value); }}
                      className="bg-white border-slate-300 text-slate-700 pr-10" 
                      placeholder="00000-000" 
                    />
                    {loadingCep && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500 animate-spin" />}
                  </div>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-600">Endereço</Label>
                  <Input value={formData.endereco} onChange={function(e) { updateForm('endereco', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="Rua/Av" />
                </div>
                <div>
                  <Label className="text-slate-600">Número</Label>
                  <Input value={formData.numero} onChange={function(e) { updateForm('numero', e.target.value); }} className="bg-white border-slate-300 text-slate-700" placeholder="123" />
                </div>
                <div>
                  <Label className="text-slate-600">Bairro</Label>
                  <Input value={formData.bairro} onChange={function(e) { updateForm('bairro', e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-600">Cidade</Label>
                  <Input value={formData.cidade} onChange={function(e) { updateForm('cidade', e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-600">UF</Label>
                  <Input value={formData.uf} onChange={function(e) { updateForm('uf', e.target.value.toUpperCase()); }} className="bg-white border-slate-300 text-slate-700" maxLength={2} />
                </div>
              </div>
            </div>
            
            {/* Plano e Serviço */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Plano e Serviço</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600">Tipo de Plano *</Label>
                  <Select value={formData.tipo_plano} onValueChange={handleTipoPlanoChange}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="MOVEL">Móvel (30 dias)</SelectItem>
                      <SelectItem value="FIBRA">Fibra (data fixa)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Plano</Label>
                  <Select value={formData.plano_id} onValueChange={handlePlanoChange}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      {planos.map(function(p) { return <SelectItem key={p.id} value={p.id}>{p.nome} - {formatCurrency(p.valor)}</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Filial</Label>
                  <Select value={formData.filial_id} onValueChange={function(v) { updateForm('filial_id', v); }}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      {filiais.map(function(f) { return <SelectItem key={f.id} value={f.id}>{f.nome} ({f.codigo})</SelectItem>; })}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Valor (R$)</Label>
                  <Input type="number" step="0.01" value={formData.valor} onChange={function(e) { updateForm('valor', e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-600">Data de Ativação</Label>
                  <Input type="date" value={formData.data_ativacao} onChange={function(e) { handleDataAtivacaoChange(e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
                  {formData.tipo_plano === 'MOVEL' && (
                    <p className="text-xs text-blue-500 mt-1">* Preencha para calcular datas automaticamente</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-600">{formData.tipo_plano === 'FIBRA' ? 'Vencimento' : 'Próx. Recarga'}</Label>
                  <Input 
                    type="date" 
                    value={formData.tipo_plano === 'FIBRA' ? formData.vencimento : formData.proxima_recarga} 
                    onChange={function(e) { updateForm(formData.tipo_plano === 'FIBRA' ? 'vencimento' : 'proxima_recarga', e.target.value); }} 
                    className="bg-white border-slate-300 text-slate-700"
                    readOnly={formData.tipo_plano === 'MOVEL'}
                  />
                  {formData.tipo_plano === 'MOVEL' && formData.data_ativacao && (
                    <p className="text-xs text-green-600 mt-1">✓ Ativação + 25 dias</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-600">Expiração de Dados</Label>
                  <Input 
                    type="date" 
                    value={formData.expiracao_dados} 
                    onChange={function(e) { updateForm('expiracao_dados', e.target.value); }} 
                    className="bg-white border-slate-300 text-slate-700"
                    readOnly={formData.tipo_plano === 'MOVEL'}
                  />
                  {formData.tipo_plano === 'MOVEL' && formData.data_ativacao && (
                    <p className="text-xs text-green-600 mt-1">✓ Ativação + 30 dias</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Status */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Status</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600">Status</Label>
                  <Select value={formData.status} onValueChange={function(v) { updateForm('status', v); }}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="ATIVO">Ativo</SelectItem>
                      <SelectItem value="INATIVO">Inativo</SelectItem>
                      <SelectItem value="BLOQUEADO">Bloqueado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Status Financeiro</Label>
                  <Select value={formData.status_financeiro} onValueChange={function(v) { updateForm('status_financeiro', v); }}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="EM_DIA">Em Dia</SelectItem>
                      <SelectItem value="EM_ATRASO">Em Atraso</SelectItem>
                      <SelectItem value="BLOQUEADO">Bloqueado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input 
                    type="checkbox" 
                    id="cliente_combo" 
                    checked={formData.cliente_combo} 
                    onChange={function(e) { handleComboChange(e.target.checked); }}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <Label htmlFor="cliente_combo" className="text-slate-600 cursor-pointer">Cliente Combo (Fibra + Móvel)</Label>
                </div>
              </div>
            </div>
            
            {/* Plano Sócio */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-green-700 uppercase mb-3">⭐ Plano Sócio</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-600">Este é um Plano Sócio? *</Label>
                  <Select value={formData.socio ? 'SIM' : 'NAO'} onValueChange={function(v) { updateForm('socio', v === 'SIM'); }}>
                    <SelectTrigger className="bg-white border-green-300 text-slate-700"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="NAO">Não</SelectItem>
                      <SelectItem value="SIM">Sim</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center">
                  <p className="text-xs text-slate-500 italic">Planos Sócios são para funcionários/donos e não geram lucro (receita = custo)</p>
                </div>
              </div>
            </div>
            
            {/* Tipo de Venda */}
            <div className="border-b border-slate-200 pb-4">
              <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Tipo de Venda</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-600">Canal de Venda</Label>
                  <Select value={formData.tipo_venda} onValueChange={function(v) { updateForm('tipo_venda', v); }}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="PRESENCIAL">Presencial (Loja)</SelectItem>
                      <SelectItem value="SHOPEE">Shopee</SelectItem>
                      <SelectItem value="ONLINE">Online</SelectItem>
                      <SelectItem value="INDICACAO">Indicação</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-slate-600">Tipo de Chip</Label>
                  <Select value={formData.tipo_chip} onValueChange={function(v) { updateForm('tipo_chip', v); }}>
                    <SelectTrigger className="bg-white border-slate-300 text-slate-700"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 z-[100]">
                      <SelectItem value="ESIM">eSIM</SelectItem>
                      <SelectItem value="FISICO">Chip Físico</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Observações */}
            <div>
              <Label className="text-slate-600">Observações</Label>
              <Input value={formData.observacoes} onChange={function(e) { updateForm('observacoes', e.target.value); }} className="bg-white border-slate-300 text-slate-700" />
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button type="button" onClick={function() { setModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600">Cancelar</Button>
              <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salvar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-800">Confirmar Exclusão</DialogTitle></DialogHeader>
          <p className="text-slate-600 py-4">Excluir {selectedCliente ? selectedCliente.nome : ''}?</p>
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button onClick={function() { setDeleteModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white">Excluir</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Gerar Boleto */}
      <Dialog open={boletoModalOpen} onOpenChange={setBoletoModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="text-slate-800">Gerar Boleto</DialogTitle></DialogHeader>
          {boletoCliente && (
            <form onSubmit={handleBoletoSubmit} className="space-y-4 py-2">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-sm text-slate-500">Cliente</p>
                <p className="font-semibold text-slate-800">{boletoCliente.nome}</p>
                {boletoCliente.plano && (
                  <p className="text-sm text-orange-600 mt-1">Plano: {boletoCliente.plano}</p>
                )}
              </div>
              {/* Aviso se cliente não tiver CPF ou Email */}
              {(!boletoCliente.cpf_cnpj || !boletoCliente.email) && (
                <div className="bg-amber-50 border border-amber-200 text-amber-700 p-3 rounded-lg text-sm">
                  <p className="font-medium mb-1">⚠️ Dados obrigatórios para boleto:</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    {!boletoCliente.cpf_cnpj && <li>CPF/CNPJ não cadastrado</li>}
                    {!boletoCliente.email && <li>Email não cadastrado</li>}
                  </ul>
                  <p className="text-xs mt-2">Edite o cliente para adicionar essas informações.</p>
                </div>
              )}
              <div>
                <Label className="text-slate-600">Valor (R$)</Label>
                <p className="text-xs text-slate-400 mb-1">Deixe vazio para usar valor do plano</p>
                <Input 
                  type="number" 
                  step="0.01" 
                  min="0"
                  value={boletoForm.valor} 
                  onChange={function(e) { setBoletoForm(function(prev) { return Object.assign({}, prev, { valor: e.target.value }); }); }} 
                  className="bg-white border-slate-300 text-slate-700" 
                  placeholder={boletoCliente.valor ? formatCurrency(boletoCliente.valor) : '0,00'}
                />
              </div>
              <div>
                <Label className="text-slate-600">Vencimento *</Label>
                <Input 
                  type="date" 
                  value={boletoForm.vencimento} 
                  onChange={function(e) { setBoletoForm(function(prev) { return Object.assign({}, prev, { vencimento: e.target.value }); }); }} 
                  className="bg-white border-slate-300 text-slate-700" 
                  required
                />
              </div>
              <div>
                <Label className="text-slate-600">Descrição</Label>
                <Input 
                  value={boletoForm.descricao} 
                  onChange={function(e) { setBoletoForm(function(prev) { return Object.assign({}, prev, { descricao: e.target.value }); }); }} 
                  className="bg-white border-slate-300 text-slate-700" 
                  placeholder="Ex: Mensalidade Janeiro/2025"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <Button type="button" onClick={function() { setBoletoModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">Cancelar</Button>
                <Button type="submit" disabled={loadingBoleto} className="bg-green-500 hover:bg-green-600 text-white">
                  {loadingBoleto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
                  Gerar Boleto + PIX
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Edição em Massa */}
      <Dialog open={bulkEditModalOpen} onOpenChange={setBulkEditModalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-800 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Editar {selectedClientes.length} Clientes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-purple-50 border border-purple-200 p-3 rounded-lg">
              <p className="text-sm text-purple-700">
                <strong>Atenção:</strong> Somente os campos preenchidos serão atualizados. Campos vazios serão ignorados.
              </p>
            </div>
            
            <div>
              <Label className="text-slate-600 font-medium">Filial</Label>
              <Select value={bulkEditData.filial_id || "nao_alterar"} onValueChange={function(v) { setBulkEditData(function(prev) { return Object.assign({}, prev, { filial_id: v === "nao_alterar" ? "" : v }); }); }}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-700 mt-1">
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 z-[100]">
                  <SelectItem value="nao_alterar">Não alterar</SelectItem>
                  {filiais.map(function(f) { 
                    return <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>; 
                  })}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-600 font-medium">Sócio</Label>
              <Select value={bulkEditData.socio || "nao_alterar"} onValueChange={function(v) { setBulkEditData(function(prev) { return Object.assign({}, prev, { socio: v === "nao_alterar" ? "" : v }); }); }}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-700 mt-1">
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 z-[100]">
                  <SelectItem value="nao_alterar">Não alterar</SelectItem>
                  <SelectItem value="true">Sim - É Sócio</SelectItem>
                  <SelectItem value="false">Não - Não é Sócio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-600 font-medium">Tipo de Venda</Label>
              <Select value={bulkEditData.tipo_venda || "nao_alterar"} onValueChange={function(v) { setBulkEditData(function(prev) { return Object.assign({}, prev, { tipo_venda: v === "nao_alterar" ? "" : v }); }); }}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-700 mt-1">
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 z-[100]">
                  <SelectItem value="nao_alterar">Não alterar</SelectItem>
                  <SelectItem value="PRESENCIAL">Presencial (Loja)</SelectItem>
                  <SelectItem value="SHOPEE">Shopee</SelectItem>
                  <SelectItem value="ONLINE">Online</SelectItem>
                  <SelectItem value="INDICACAO">Indicação</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-slate-600 font-medium">Tipo de Chip</Label>
              <Select value={bulkEditData.tipo_chip || "nao_alterar"} onValueChange={function(v) { setBulkEditData(function(prev) { return Object.assign({}, prev, { tipo_chip: v === "nao_alterar" ? "" : v }); }); }}>
                <SelectTrigger className="bg-white border-slate-300 text-slate-700 mt-1">
                  <SelectValue placeholder="Não alterar" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200 z-[100]">
                  <SelectItem value="nao_alterar">Não alterar</SelectItem>
                  <SelectItem value="ESIM">eSIM</SelectItem>
                  <SelectItem value="FISICO">Chip Físico</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button type="button" onClick={function() { setBulkEditModalOpen(false); }} variant="outline" className="bg-white border-slate-300 text-slate-600 hover:bg-slate-50">
                Cancelar
              </Button>
              <Button type="button" onClick={handleBulkEditSubmit} disabled={bulkEditLoading} className="bg-purple-600 hover:bg-purple-700 text-white">
                {bulkEditLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckSquare className="w-4 h-4 mr-2" />}
                Atualizar {selectedClientes.length} Clientes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ClientesPage;
